/**
 * Navigation Preferences API
 *
 * API functions for user navigation preferences.
 */

import { apiClient } from '../api-client';
import type {
  NavigationPreferences,
  NavigationPreferencesResponse,
  ToggleFavoriteRequest,
  ToggleVisibilityRequest,
  UpdateSortOrderRequest,
} from '@/types/navigation';

/**
 * Get user's navigation preferences
 */
export async function getNavigationPreferences(): Promise<NavigationPreferences> {
  const response = await apiClient.get<NavigationPreferencesResponse>(
    '/api/v1/navigation/preferences'
  );
  return response.data.preferences;
}

/**
 * Update all navigation preferences
 */
export async function updateNavigationPreferences(
  preferences: NavigationPreferences
): Promise<NavigationPreferences> {
  const response = await apiClient.put<NavigationPreferencesResponse>(
    '/api/v1/navigation/preferences',
    { preferences }
  );
  return response.data.preferences;
}

/**
 * Toggle tool favorite status
 */
export async function toggleFavorite(
  toolId: string,
  isFavorite: boolean
): Promise<NavigationPreferences> {
  const response = await apiClient.patch<NavigationPreferencesResponse>(
    '/api/v1/navigation/preferences/favorite',
    { toolId, isFavorite } satisfies ToggleFavoriteRequest
  );
  return response.data.preferences;
}

/**
 * Toggle tool visibility
 */
export async function toggleVisibility(
  toolId: string,
  isHidden: boolean
): Promise<NavigationPreferences> {
  const response = await apiClient.patch<NavigationPreferencesResponse>(
    '/api/v1/navigation/preferences/visibility',
    { toolId, isHidden } satisfies ToggleVisibilityRequest
  );
  return response.data.preferences;
}

/**
 * Update sort order
 */
export async function updateSortOrder(
  sortOrder: Record<string, number>
): Promise<NavigationPreferences> {
  const response = await apiClient.patch<NavigationPreferencesResponse>(
    '/api/v1/navigation/preferences/sort',
    { sortOrder } satisfies UpdateSortOrderRequest
  );
  return response.data.preferences;
}

/**
 * Reset preferences to defaults
 */
export async function resetNavigationPreferences(): Promise<NavigationPreferences> {
  const response = await apiClient.post<NavigationPreferencesResponse>(
    '/api/v1/navigation/preferences/reset'
  );
  return response.data.preferences;
}

// ===========================================
// WORKSPACE TOOLS
// ===========================================

export interface WorkspaceTool {
  id: string;
  name: string;
  icon: string;
  path: string;
  category: 'tool' | 'quick_link';
  badge?: string | null;
  is_disabled: boolean;
  sort_order: number;
  computed_sort_order: number; // Auto-computed for new tools (negative = top)
}

export interface WorkspaceToolsResponse {
  success: boolean;
  tools: WorkspaceTool[];
}

/**
 * Get workspace tools with auto-expiring badges
 */
export async function getWorkspaceTools(): Promise<WorkspaceTool[]> {
  const response = await apiClient.get<WorkspaceToolsResponse>('/api/v2/navigation/tools');
  return response.data.tools;
}
